import { inject, observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Notice, Spinner } from 'telia-front-react';
import CouponStore from '../stores/CouponStore';
import ProductStore from '../stores/ProductStore';
import SsoStore from '../stores/SsoStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import Telia1Store from '../stores/Telia1Store';
import { FetchStatus } from '../util/FetchStatus';

export interface HasProductProps {
  productCategoryCodes?: string[];
  productOfferingCodes?: string[];
  productOfferingName?: string;
  telia1Products?: string[];
  telia1CouponCodes?: string[];
  origins?: string[];
  matchAll?: boolean;
  not?: boolean;
  showError?: boolean;
  showSpinner?: boolean;
  productStore?: ProductStore;
  telia1Store?: Telia1Store;
  ssoStore?: SsoStore;
  couponStore?: CouponStore;
  children?: React.ReactNode;
}

const HasProduct: React.FC<HasProductProps> = ({
  productCategoryCodes = [],
  productOfferingCodes = [],
  productOfferingName = '',
  telia1CouponCodes = [],
  telia1Products = [],
  origins = [],
  matchAll = false,
  not = false,
  productStore,
  telia1Store,
  couponStore,
  showError = false,
  showSpinner = false,
  ssoStore,
  children,
}) => {
  const isTelia1 = telia1Products.length;
  const hasCoupons = telia1CouponCodes.length;
  const isProductInventory = productCategoryCodes.length || productOfferingCodes.length || productOfferingName;
  const translate = React.useContext(TranslationsContext).translate;

  useEffect(() => {
    if (isTelia1) {
      telia1Store.loadTelia1();
    }
    if (hasCoupons) {
      couponStore.loadTelia1Coupons();
    }
    if (isProductInventory) {
      productStore.checkCustomerProducts(productCategoryCodes, productOfferingCodes, productOfferingName, origins);
    }
  }, [ssoStore.customerId]);

  if (!productCategoryCodes.length && !productOfferingCodes.length && !productOfferingName.length && !telia1Products.length && !telia1CouponCodes.length) {
    return null;
  }
  if (showSpinner) {
    if (
      (isProductInventory && [FetchStatus.FETCHING, FetchStatus.UNINITIALIZED].includes(productStore.customerProductsStatus)) ||
      telia1Store.isFetching ||
      couponStore.isFetching
    ) {
      return <Spinner />;
    }
  }

  if (
    (isProductInventory && productStore.customerProductsStatus === FetchStatus.ERROR) ||
    (isTelia1 && telia1Store.isErrored) ||
    (hasCoupons && couponStore.isErrored)
  ) {
    return showError ? <Notice type={'error'}>{translate('general.error.message')}</Notice> : null;
  }

  const shouldShow = (): boolean => {
    const matching = matchAll ? 'every' : 'some';
    const checkIfArrayMatches = (arrayFromProps: string[], arrayFromStore: string[]) => {
      return arrayFromProps[matching]((item) => arrayFromStore.includes(item));
    };
    if (hasCoupons && !isTelia1) {
      return checkIfArrayMatches(telia1CouponCodes, couponStore.telia1CouponCodes);
    }
    if (isTelia1 && hasCoupons) {
      return checkIfArrayMatches(telia1Products, telia1Store.products) && checkIfArrayMatches(telia1CouponCodes, couponStore.telia1CouponCodes);
    }
    if (isTelia1 && !hasCoupons) {
      return checkIfArrayMatches(telia1Products, telia1Store.products);
    } else {
      return productStore.customerProducts[matching](
        (product) =>
          productCategoryCodes.some((category) => product.productCategoryCodes.includes(category)) ||
          productOfferingCodes.includes(product.productOfferingCode) ||
          productOfferingName === product.productOfferingName,
      );
    }
  };

  return not !== shouldShow() && <>{children}</>;
};

export default inject('productStore', 'telia1Store', 'ssoStore', 'couponStore')(observer(HasProduct));
